import * as fs from "node:fs";
import * as path from "node:path";

export const SEED_MANIFEST_PATH = ".m8t/seed-manifest.json";

export const SEED_PATHS = ["skills", "references", "targets"] as const;

export interface SeedLineage {
  name: string;
  /** A real, fetchable m8t-labs/m8t commit — the rev this repo's seed was last brought to. */
  refreshedToCommit: string;
  refreshedToVersion: string;
  /** Repo-relative seed-owned paths (incl. SEED_MANIFEST_PATH itself). The discriminator. */
  ownedPaths: string[];
}

export interface SeedManifest {
  schemaVersion: 1;
  seeds: SeedLineage[];
}

function isLineage(v: unknown): v is SeedLineage {
  if (!v || typeof v !== "object") return false;
  const l = v as Record<string, unknown>;
  return typeof l.name === "string" && typeof l.refreshedToCommit === "string"
    && typeof l.refreshedToVersion === "string" && Array.isArray(l.ownedPaths)
    && l.ownedPaths.every((p) => typeof p === "string");
}

export function parseSeedManifest(text: string | null): SeedManifest | null {
  if (!text?.trim()) return null;
  let parsed: unknown;
  try { parsed = JSON.parse(text); } catch { return null; }
  if (!parsed || typeof parsed !== "object") return null;
  const m = parsed as Record<string, unknown>;
  if (m.schemaVersion !== 1 || !Array.isArray(m.seeds) || !m.seeds.every(isLineage)) return null;
  return { schemaVersion: 1, seeds: m.seeds };
}

export function buildSeedManifest(seeds: SeedLineage[]): SeedManifest {
  return { schemaVersion: 1, seeds };
}

export function upsertLineage(manifest: SeedManifest | null, lineage: SeedLineage): SeedManifest {
  const prior = manifest?.seeds ?? [];
  const others = prior.filter((s) => s.name !== lineage.name);
  return { schemaVersion: 1, seeds: [...others, lineage].sort((a, b) => a.name.localeCompare(b.name)) };
}

export function serializeSeedManifest(m: SeedManifest): string {
  return JSON.stringify(m, null, 2) + "\n";
}

function walkFiles(root: string, rel = ""): string[] {
  const abs = path.join(root, rel);
  if (!fs.existsSync(abs)) return [];
  const out: string[] = [];
  for (const entry of fs.readdirSync(abs, { withFileTypes: true })) {
    const childRel = rel ? `${rel}/${entry.name}` : entry.name;
    if (entry.isDirectory()) out.push(...walkFiles(root, childRel));
    else if (entry.isFile()) out.push(childRel);
  }
  return out;
}

/** Repo-relative seed-owned paths under the manifest's SEED_PATHS subtrees, plus the marker.
 *  Structurally cannot include memory/ etc. — it only walks SEED_PATHS. */
export function deriveOwnedPaths(seedDir: string): string[] {
  const owned = new Set<string>([SEED_MANIFEST_PATH]);
  for (const sub of SEED_PATHS) {
    for (const rel of walkFiles(path.join(seedDir, sub))) owned.add(`${sub}/${rel}`);
  }
  return [...owned].sort((a, b) => a.localeCompare(b));
}

export function isSeedOwned(repoRelPath: string, ownedPaths: string[]): boolean {
  return ownedPaths.includes(repoRelPath);
}
